import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Slider,
  FormHelperText
} from '@mui/material';
import { Description as ContentIcon } from '@mui/icons-material';

function ContentGenerationDialog({ open, onClose, onContentGenerated, accountId }) {
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [contentType, setContentType] = useState('text');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [maxTokens, setMaxTokens] = useState(1000);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/content/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          prompt,
          title: title || 'Untitled Content',
          contentType,
          maxTokens,
          accountId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate content');
      }
      
      const data = await response.json();
      
      if (onContentGenerated) {
        onContentGenerated(data);
      }
      
      // Reset form
      setPrompt('');
      setTitle('');
      setContentType('text');
      setMaxTokens(1000);
      
      onClose();
    } catch (err) {
      console.error('Content generation error:', err);
      setError(err.message || 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMaxTokensChange = (event, newValue) => {
    setMaxTokens(newValue);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={loading ? null : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <ContentIcon sx={{ mr: 1 }} />
          Generate AI Content
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            label="Title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            margin="normal"
            placeholder="Enter a title for your content"
          />
          
          <TextField
            label="Prompt"
            fullWidth
            multiline
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            margin="normal"
            required
            placeholder="Describe the content you want to generate..."
            helperText="Be specific about the type of content, tone, style, and purpose"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Content Type</InputLabel>
            <Select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              label="Content Type"
            >
              <MenuItem value="text">Plain Text</MenuItem>
              <MenuItem value="html">HTML</MenuItem>
              <MenuItem value="markdown">Markdown</MenuItem>
              <MenuItem value="blog">Blog Post</MenuItem>
              <MenuItem value="product">Product Description</MenuItem>
              <MenuItem value="seo">SEO Content</MenuItem>
            </Select>
          </FormControl>
          
          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>
              Maximum Length (tokens)
            </Typography>
            <Slider
              value={maxTokens}
              onChange={handleMaxTokensChange}
              aria-labelledby="max-tokens-slider"
              valueLabelDisplay="auto"
              step={100}
              marks
              min={100}
              max={4000}
            />
            <FormHelperText>
              Longer content will cost more tokens. 1000 tokens is approximately 750 words.
            </FormHelperText>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading || !prompt.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Generating...' : 'Generate Content'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ContentGenerationDialog; 